import { inject, Injector } from '@angular/core';
import type { CanActivateFn } from '@angular/router';
import { NavigationService } from '@core/navigation/services/navigation.service';
import { signalToObservable } from '@core/utils/signal.utilities';
import {
  createAsyncErrorHandler,
  createErrorMetadata,
  ErrorSeverityMap,
} from '@kerberos-compliance/compliance-shared-ui';
import { combineLatest, lastValueFrom } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { ContractStore } from '@/contract/store/contract.store';

/**
 * A route guard function that prevents navigation to a route if there is an active contract.
 *
 * This guard checks the current state of the `ContractStore` to determine if the user has an active
 * contract. If an active contract is detected, the guard redirects the user to the dashboard and denies
 * access to the route. If no active contract is found, navigation to the target route is allowed.
 *
 * The guard uses Observable streams to monitor the loading state, selected contract, errors, and query
 * information from the `ContractStore`. Navigation is allowed only if the loading process is complete and
 * there is no active contract or error in the contract data.
 *
 * Error handling is managed using an asynchronous error handler that redirects the user to the logout
 * route on failure. Any errors encountered during the execution are tracked with metadata for observability.
 *
 * @type {CanActivateFn}
 * @returns {Promise<boolean>} A boolean indicating whether navigation to the route is allowed.
 */
export const denyActiveContractGuard: CanActivateFn = async () => {
  const navigationService = inject(NavigationService);
  const contractStore = inject(ContractStore);
  const injector = inject(Injector);
  const asyncErrorHandler = createAsyncErrorHandler();

  const result = await asyncErrorHandler(
    async () => {
      const loading$ = signalToObservable(injector, contractStore.loading);
      const selectedContract$ = signalToObservable(injector, contractStore.selectedContract);
      const error$ = signalToObservable(injector, contractStore.error);
      const query$ = signalToObservable(injector, contractStore.query);

      const [, selected] = await lastValueFrom(
        combineLatest([loading$, selectedContract$, error$, query$]).pipe(
          filter(([loading, selected_, error, query]) => {
            if (loading) {
              return false;
            }
            if (selected_ || error) {
              return true;
            }
            // Allow navigation only when we are not expecting contract data
            return !query?.organizationId;
          }),
          take(1),
        ),
      );

      const isActive = !!selected?.isActive;
      if (isActive) {
        void navigationService.goToDashboard();
        return false;
      }
      return true;
    },
    {
      metadata: createErrorMetadata('contractNotActiveOnlyGuard', 'Error fetching contract', ErrorSeverityMap.High),
      onError: () => void navigationService.goToLogout(),
    },
  );

  return result ?? false;
};
